<template>
    <responsive-button
        class="ml-2"
        :icon="['fas', 'wand-magic-sparkles']"
        @click="openModal()"
        variant="outline-primary"
        :with-space="false"
        v-if="aiScanAllowed"
    ></responsive-button>

    <modal
        id="ai-scan-template-modal"
        :title="aiScanTitle"
        size="xl"
    >
        <div v-if="isLoading" class="text-center mt-3 d-flex flex-column align-items-center">
            <b-spinner variant="primary" style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
            <p class="mt-4">This may take a few moments. Please wait.</p>
        </div>

        <template v-else>
            <div class="mt-3" v-if="!scanResults.length">
                <p>
                    The AI Scan analyzes your communication template to help ensure professional, error-free messaging. 
                    Our AI will review all text content in your template and provide intelligent suggestions for improvement.
                </p>
                <p class="mb-2">
                    <strong>What the AI Scan checks for:</strong>
                </p>
                <ul class="mb-3">
                    <li>Spelling and grammatical errors</li>
                    <li>Clarity and readability improvements</li>
                    <li>Professional tone and language</li>
                    <li>Sentence structure optimization</li>
                    <li>Word choice enhancements</li>
                </ul>
                <p>
                    You can review each suggestion individually and choose to accept or decline changes. 
                    Click "Scan Now" to begin analyzing your template.
                </p>
            </div>

            <template v-if="scanResults.length > 0">
                <div>
                    <template v-for="(result, index) in scanResults">
                        <b-card 
                            v-if="result.has_changes"
                            :key="index"
                            class="mb-4"
                        >
                            <div class="d-flex align-items-center justify-content-between">
                                <div class="d-flex flex-column align-items-start">
                                    <h5 class="card-title mb-0">
                                        <i class="fas fa-file-text text-primary me-2"></i>
                                        {{ getBlockTitle(result, index) }}
                                    </h5>
                                    <p class="text m-0" v-if="result.accepted">
                                        <i class="fas fa-check-circle text-success me-1"></i>
                                        Change Accepted
                                    </p>
                                    <p class="text m-0" v-if="result.ignored">
                                        <i class="fas fa-times-circle text-muted me-1"></i>
                                        Change Declined
                                    </p>
                                </div>
                                <b-button 
                                    size="sm" 
                                    variant="outline-primary" 
                                    @click="result.opened = !result.opened" 
                                    v-if="result.accepted || result.ignored"
                                >
                                    <font-awesome-icon v-if="!result.opened" :icon="['fas', 'chevron-down']" />
                                    <font-awesome-icon v-else :icon="['fas', 'chevron-up']" />
                                </b-button>
                            </div>

                            <b-collapse v-model="result.opened">
                                <div class="pt-3">
                                    <div class="text-block">
                                        <DiffViewer
                                            :original-template="result.original_text"
                                            :corrected-template="result.corrected_text"
                                        />

                                        <template v-if="result.feedback">
                                            <h5 class="card-title mb-2 mt-3">AI Feedback</h5>
                                            <div class="feedback-content">
                                                <div class="d-flex align-items-start">
                                                    <i class="fas fa-lightbulb text-warning me-2 mt-1"></i>
                                                    <p class="mb-0">{{ result.feedback }}</p>
                                                </div>
                                            </div>
                                        </template>
                                    </div>

                                    <div class="button-group mt-3" v-if="!result.accepted">
                                        <b-button 
                                            variant="primary" 
                                            class="me-2"
                                            @click="applyChanges(result)"
                                        >
                                            Accept
                                        </b-button>
                                        <b-button 
                                            v-if="!result.ignored && !result.accepted"
                                            variant="outline-secondary" 
                                            @click="declineChange(result)"
                                        >
                                            Decline
                                        </b-button>
                                    </div>
                                </div>
                            </b-collapse>
                        </b-card>
                    </template>

                    <div
                        v-if="scanResults.every(r => !r.has_changes)"
                        style="display: flex; flex-direction: column; justify-content: center; align-items: center"
                    >
                        <font-awesome-icon
                            :icon="['far', 'circle-check']"
                            style="width: 50px; height: 50px; padding-bottom: 20px; color: #0d47a1"
                        ></font-awesome-icon>
                        <p>Everything looks good; no issues were found.</p>
                        <small>Some issues may go undetected—please review manually if needed.</small>
                    </div>
                </div>
            </template>
        </template>




        <template #modal-footer="{ ok }" v-if="!isLoading">
            <div class="w-100">
                <b-button
                    variant="primary"
                    class="float-right px-4 mx-2"
                    @click="startAiScan()"
                    v-if="!scanResults.length"
                >
                    Scan Now
                </b-button>

                <template v-if="scanResults.length">
                    <b-button
                        v-if="hasPendingResults"
                        variant="primary"
                        class="float-right px-4 mx-2"
                        @click="applyChanges(null)"
                    >
                        Accept All Changes ({{ scanResults.filter(r => r.has_changes && !r.accepted && !r.ignored).length }})
                    </b-button>
                    <b-button
                        variant="secondary"
                        class="float-right px-4 mx-2"
                        @click="ok()"
                    >
                        Close
                    </b-button>
                </template>
            </div>
        </template>
    </modal>
</template>

<script>
import { mapGetters } from 'vuex';
import FormInput from '../../generic/form-input/form-input.vue';
import formSelect from '../../generic/form-select/form-select.vue';
import FormCheckbox from '../../generic/form-checkbox/form-checkbox.vue';
import { UNLAYER_TEMPLATE_BY_ID_ENDPOINT } from '../../../endpoints';
import Modal from '../../generic/modal/modal.vue';
import api from '../../../api';
import DiffViewer from './diff-viewer.vue';
import { formatResponseError } from '../../../utils/api';
import ResponsiveButton from '../../generic/responsive-button/responsive-button.vue';

export default {
    components: {
        FormInput,
        formSelect,
        Modal,
        FormCheckbox,
        DiffViewer,
        ResponsiveButton,
    },
    name: 'UnlayerTemplatesAiScanModal',
    props: {
        templateId: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            isLoading: false,
            scanResults: [],
            totalTextBlocks: 0,
            blocksWithChanges: 0,
            aiScanAllowed: false,
        };
    },

    computed: {
        ...mapGetters('header', ['userIsAdmin']),
        
        hasPendingResults() {
            return this.scanResults.some(result => result.has_changes && !result.accepted && !result.ignored);
        },
        aiScanTitle() {
            return `AI Scan for Communications${this.isLoading ? ' - Loading...' : ''}`;
        },
    },
    async mounted() {
        const { data } = await api.get(`${UNLAYER_TEMPLATE_BY_ID_ENDPOINT()}ai-scan-permission/`);
        this.aiScanAllowed = data.ai_scan_allowed;
    },
    methods: {
        async startAiScan() {
            if (!this.aiScanAllowed) {
                this.errorToast({ text: 'AI Scan is not allowed.' });
                this.$bvModal.hide('ai-scan-template-modal');
                return;
            }
            try {
                this.isLoading = true;
                const { data } = await api.post(UNLAYER_TEMPLATE_BY_ID_ENDPOINT(this.templateId) + 'ai-scan/');

                this.scanResults = data.results.map(result => ({
                    ...result,
                    accepted: false,
                    ignored: false,
                    opened: true,
                }));
                this.totalTextBlocks = data.total_text_blocks || 0;
                this.blocksWithChanges = data.blocks_with_changes || 0;
            } catch (error) {
                console.error('Error during AI scan:', error);
                const { generic } = formatResponseError(error);
                this.errorToast({ text: generic || 'Could not scan template with AI.' });
            } finally {
                this.isLoading = false;
            }
        },

        async applyChanges(result) {
            let payload = {
                results: result ? [result] : this.scanResults,
            };

            try {
                const endpoint = UNLAYER_TEMPLATE_BY_ID_ENDPOINT(this.templateId) + 'update-template/';
                await api.post(endpoint, payload);

                if (result) {
                    result.accepted = true;
                    result.opened = false;
                    result.ignored = false;
                } else {
                    this.scanResults.forEach(res => {
                        res.accepted = true;
                        res.opened = false;
                        res.ignored = false;
                    });
                }

                this.$emit('submit', payload);
                this.successToast({ text: 'Template saved successfully' });
            } catch (e) {
                console.error('Error applying changes:', e);
                const { generic } = formatResponseError(e);
                this.errorToast({ text: generic || 'Could not save changes.' });
            }
        },

        resetModal() {
            this.scanResults = [];
            this.totalTextBlocks = 0;
            this.blocksWithChanges = 0;
            this.isLoading = false;
        },

        declineChange(result) {
            result.ignored = true;
            result.opened = false;
        },

        getBlockTitle(result, index) {
            const suggestionsWithChanges = this.scanResults
                .slice(0, index + 1)
                .filter(r => r.has_changes);
            return `AI Suggestion ${suggestionsWithChanges.length}`;
        },

        openModal() {
            if (!this.aiScanAllowed) {
                this.errorToast({ text: 'AI Scan is not allowed.' });
                this.$bvModal.hide('ai-scan-template-modal');
                return;
            }
            this.resetModal();
            this.$bvModal.show('ai-scan-template-modal');
        }
    },
    watch: {
        templateId(newVal) {
            if (newVal) {
                this.resetModal();
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.card-title {
    font-size: 1.1rem;
    font-weight: 600;
}

.text-block {
    border-left: 3px solid #e9ecef;
    padding-left: 1rem;
}

.button-group {
    display: flex;
    gap: 0.5rem;
}

.feedback-content {
    background-color: #f8f9ff;
    border: 1px solid #e6e6ff;
    border-radius: 6px;
    padding: 1rem;
    margin-top: 0.5rem;
}
</style>
